import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useStore } from "react-redux";
import { selectOrderByUuid } from "@/modules/pg/shared/planningTable/reducers/planningTable/selectors";

import EditableTextArea from "@/modules/shared/ui/EditableTextArea";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import * as Auth from "@/lib/auth";
import { useResolvedOrderComment } from "@/lib/custom-hooks/queryComment";
import { onSaveComment } from "@/modules/pg/shared/planningTable/modals/PlanningTableEditModal";

import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";

type Props = {
    order_uuid: string;
    comment: string;
};

const OrderCommentTextArea = (props: Props) => {
    const { order_uuid, comment } = props;

    const { resolvedComment, updateOrderComment } = useResolvedOrderComment(order_uuid, comment);

    const [inputComment, setInputComment] = useState(resolvedComment);

    const order = useSelector((state: ReduxState) => selectOrderByUuid(state, order_uuid));
    const store = useStore<ReduxState>();

    useEffect(() => {
        setInputComment(resolvedComment);
    }, [resolvedComment]);

    const handleTextareaChange = useCallback((value: string) => {
        setInputComment(value);
    }, []);

    const handleSave = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();

            if (!order) {
                return;
            }

            try {
                await updateOrderComment.mutateAsync({ order, comment: inputComment });
                onSaveComment(order_uuid, inputComment, store);
            } catch (e) {
                // Error is handled by the mutation hook rollback.
                console.log((e as Error).message);
            }
        },
        [order, inputComment, order_uuid, store, updateOrderComment]
    );

    const isDisabled = !Auth.hasPermission(Auth.PERMISSION_NAMES.OrderEdit);
    const showSave = inputComment !== resolvedComment;

    return (
        <div className="d-flex flex-column gap-1">
            <div className="font-weight-bold">{translate("common.comment", "Comment")}:</div>
            <div className="d-flex gap-2">
                <EditableTextArea
                    value={inputComment}
                    onChange={handleTextareaChange}
                    isDisabled={isDisabled}
                    title={translate("common.comment", "Comment")}
                />
                {showSave && (
                    <button id="comment-edit-button" className="btn btn-outline-primary" onClick={handleSave}>
                        {translate("common.save", "Save")}
                    </button>
                )}
            </div>
        </div>
    );
};

export default OrderCommentTextArea;
